import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { ConfirmDialogComponent } from '../core/confirm-dialog.component';

interface BulkOverview {
  eligible: number;
  failed: number;
  awaiting: number;
  confirmed: number;
  skipped: number;
  updatableExisting: number;
}

interface BulkJob {
  jobId: number;
  status: 'running' | 'completed' | 'failed' | 'canceled';
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  skipped: number;
  pending: number;
  sending: number;
  throttle: number;
  lastError: string | null;
  mode: 'send_new' | 'update_existing';
}

interface SettingsView {
  HotelCode: string;
  HotelName: string;
  VmApiBaseUrl: string;
}

interface VmSessionView {
  active: boolean;
  propertyId: string | null;
  baseUrl: string | null;
  username: string | null;
}

interface MergeSummary {
  namesChecked: number;
  mergeCallsAttempted: number;
  mergeCallsSucceeded: number;
  mergeCallsFailed: number;
}

@Component({
  selector: 'app-reservations-bulk',
  imports: [RouterLink, ConfirmDialogComponent],
  template: `
    <section class="card">
      <header class="head">
        <div>
          <h1>Bulk Send Reservations</h1>
          <p class="hint">Send all eligible staged reservations to Visual Matrix.</p>
        </div>
        <div class="actions">
          <a routerLink="/reservations" class="button secondary">Back to Reservations</a>
          @if (!job()) {
            <button type="button" (click)="openSendAllPrompt()" [disabled]="busy() || (!overview().eligible && !overview().failed)">{{ busy() ? 'Starting…' : 'Send All' }}</button>
            @if (overview().updatableExisting > 0) {
              <button type="button" (click)="start('update_existing')" [disabled]="busy()">{{ busy() ? 'Starting…' : 'Send Updates For Existing' }}</button>
            }
          } @else if (job()?.status === 'running') {
            <button type="button" class="danger" (click)="cancel()" [disabled]="busy()">Cancel</button>
          }
        </div>
      </header>

      @if (message()) {
        <div class="msg" [class.err]="isError()">{{ message() }}</div>
      }

      @if (settings().HotelCode || settings().HotelName) {
        <div class="property-note">
          <div class="prop-id">{{ settings().HotelCode }}</div>
          <div class="prop-name">{{ settings().HotelName }}</div>
        </div>
      }

      @if (!job()) {
        <div class="stats">
          <div><strong>{{ overview().eligible }}</strong><span>Eligible to Send</span></div>
          <div><strong>{{ overview().failed }}</strong><span>Errored (Retry Eligible)</span></div>
          <div><strong>{{ overview().awaiting }}</strong><span>Awaiting Confirmation</span></div>
          <div><strong>{{ overview().confirmed }}</strong><span>Confirmed</span></div>
          <div><strong>{{ overview().skipped }}</strong><span>Skipped (Exists)</span></div>
        </div>
        <div class="session-note">
          Method: <code>VM_DIRECT_API</code> · Login URL: <code>{{ settings().VmApiBaseUrl || 'https://login.vmcloudpms.com' }}</code>
          @if (vmSession().active) {
            <span> · API URL: <code>{{ vmSession().baseUrl }}</code> · Property: <code>{{ vmSession().propertyId }}</code> · User: <code>{{ vmSession().username }}</code></span>
          } @else {
            <span> · No active VM session.</span>
          }
        </div>
        <div class="subhint">Throttle: <code>BulkJob.ThrottlePerSec</code> (default 3/sec). You can leave this page open while it runs.</div>
      } @else {
        <section class="panel">
          <div class="panel-head">
            <div>
              <h2>Bulk Send {{ job()!.status.toUpperCase() }}</h2>
              <p class="hint">Mode: {{ job()!.mode === 'update_existing' ? 'Send Updates For Existing' : 'Send All' }} · Throttle: {{ job()!.throttle }}/sec</p>
            </div>
            <div class="badge" [class.ok]="job()!.status === 'completed'" [class.warn]="job()!.status === 'failed'" [class.muted]="job()!.status === 'canceled'">
              {{ job()!.status }}
            </div>
          </div>

          <div class="stats">
            <div><strong>{{ job()!.succeeded }}</strong><span>Succeeded</span></div>
            <div><strong>{{ job()!.failed }}</strong><span>Failed</span></div>
            <div><strong>{{ job()!.skipped }}</strong><span>Skipped</span></div>
            <div><strong>{{ job()!.total }}</strong><span>Total</span></div>
            <div><strong>{{ job()!.processed }}</strong><span>Processed</span></div>
            <div><strong>{{ job()!.pending }}</strong><span>Pending</span></div>
            <div><strong>{{ job()!.sending }}</strong><span>Sending</span></div>
          </div>

          <div class="progress">
            <div class="bar">
              <div class="fill" [style.width.%]="progressPercent()"></div>
            </div>
            <div class="progress-label">{{ progressPercent() }}% complete</div>
          </div>

          @if (job()!.lastError) {
            <div class="note">{{ job()!.lastError }}</div>
          }

          @if (job()!.status !== 'running') {
            <div class="actions end-actions">
              <a routerLink="/logs" class="button secondary">View Logs</a>
              <a routerLink="/reservations" class="button secondary">Back to Reservations</a>
              <button type="button" (click)="goLanding()">Back to Bulk Start</button>
              @if (job()!.succeeded > 0) {
                <button type="button" (click)="openMergePrompt()">Run Guest Duplicate Merge</button>
              }
              @if (job()!.skipped > 0) {
                <button type="button" (click)="start('update_existing')">Send Updates For Existing</button>
              }
            </div>
          }
        </section>
      }
    </section>
    <app-confirm-dialog
      [open]="mergeConfirmOpen()"
      title="Run Guest Duplicate Merge?"
      message="Would you like to run merge on potential guest history duplicates for successful sends in this bulk job?"
      confirmLabel="Yes, Run Merge"
      cancelLabel="No"
      (confirm)="confirmMerge()"
      (cancel)="cancelMerge()"
    />
    @if (startModePromptOpen()) {
      <div class="vm-modal__overlay" (click)="cancelSendAllPrompt()" aria-hidden="true"></div>
      <section class="vm-modal" role="dialog" aria-modal="true" aria-labelledby="bulk-send-mode-title">
        <header class="vm-modal__header">
          <h2 class="vm-modal__title" id="bulk-send-mode-title">Bulk Send Scope</h2>
          <button type="button" class="vm-modal__close" (click)="cancelSendAllPrompt()" aria-label="Close dialog">×</button>
        </header>
        <div class="vm-modal__body">
          <p>Choose how to handle errored reservations before starting bulk send.</p>
        </div>
        <footer class="vm-modal__footer">
          <button type="button" class="vm-button vm-button--secondary" (click)="startSendAll(false)">Send Unsent Only</button>
          <button type="button" class="vm-button vm-button--primary" (click)="startSendAll(true)">Clear Errors + Retry Failed</button>
          <button type="button" class="vm-button vm-button--ghost" (click)="cancelSendAllPrompt()">Cancel</button>
        </footer>
      </section>
    }
  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:12px;padding:12px;display:grid;gap:10px}
    .head,.panel-head{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;flex-wrap:wrap}
    h1,h2{margin:0}
    .hint{margin:2px 0 0;color:var(--muted);font-size:13px}
    .actions{display:flex;gap:10px;flex-wrap:wrap}
    .button,button{height:36px;display:inline-flex;align-items:center;justify-content:center;border:0;border-radius:8px;background:var(--primary);color:#fff;font-weight:700;padding:0 12px;text-decoration:none;cursor:pointer}
    .secondary{background:#6b778c}
    .danger{background:#8f3030}
    .msg{padding:8px 10px;border-radius:8px;background:#eef4ff;color:#0a2f6b;border:1px solid #c7d7f5;font-size:13px}
    .msg.err{background:#fdecec;color:#8a1f1f;border-color:#f2c4c4}
    .property-note{display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:8px 10px;border:1px solid #dbe5ff;border-radius:10px;background:#f8fbff}
    .prop-id{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-weight:700}
    .prop-name{color:#334155}
    .stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px}
    .stats div,.panel{padding:9px;border:1px solid #e2e8f4;border-radius:10px;background:#f8fbff}
    .stats strong{font-size:26px;line-height:1.1;display:block}
    .stats span{color:var(--muted);font-size:12px;text-transform:uppercase;letter-spacing:.04em}
    .subhint,.progress-label,.note,.session-note{font-size:12px;color:var(--muted)}
    .progress{display:grid;gap:8px}
    .bar{height:10px;border-radius:999px;background:#d8e1ef;overflow:hidden}
    .fill{height:100%;background:linear-gradient(90deg,#164b8f,#3f8cff)}
    .badge{padding:8px 12px;border-radius:999px;background:#d8e1ef;color:#26415f;text-transform:capitalize;font-weight:700}
    .badge.ok{background:#d9f4df;color:#195b29}
    .badge.warn{background:#fde3df;color:#8a1f1f}
    .badge.muted{background:#eceff4;color:#495568}
    .note{padding:8px 10px;border-radius:8px;background:#fff7df;border:1px solid #edd79b;color:#735706}
    .end-actions{margin-top:8px}
    code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
    .vm-modal__overlay{position:fixed;inset:0;background:rgba(18,32,54,.42);z-index:40}
    .vm-modal{position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);width:min(92vw,520px);background:var(--panel);border:1px solid var(--line);border-radius:16px;box-shadow:0 24px 60px rgba(18,32,54,.24);z-index:41;display:grid}
    .vm-modal__header,.vm-modal__footer{padding:14px 16px;display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap}
    .vm-modal__header{border-bottom:1px solid var(--line)}
    .vm-modal__body{padding:16px;color:var(--text)}
    .vm-modal__title{margin:0;font-size:18px;line-height:1.2}
    .vm-modal__close{border:0;background:transparent;color:var(--muted);font-size:24px;line-height:1;cursor:pointer;padding:0 4px}
    .vm-modal__footer{border-top:1px solid var(--line);justify-content:flex-end}
    .vm-button{height:40px;border:0;border-radius:10px;padding:0 14px;font-weight:700;cursor:pointer}
    .vm-button--primary{background:var(--primary);color:var(--primary-ink)}
    .vm-button--secondary{background:#6b778c;color:#fff}
    .vm-button--ghost{background:#e5eaf3;color:#29405f}
    @media (max-width: 900px){ .stats{grid-template-columns:1fr 1fr} }
    @media (max-width: 640px){ .stats{grid-template-columns:1fr} }
  `,
})
export class ReservationsBulkComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly busy = signal(false);
  readonly message = signal('');
  readonly isError = signal(false);
  readonly overview = signal<BulkOverview>({ eligible: 0, failed: 0, awaiting: 0, confirmed: 0, skipped: 0, updatableExisting: 0 });
  readonly job = signal<BulkJob | null>(null);
  readonly mergeConfirmOpen = signal(false);
  readonly startModePromptOpen = signal(false);
  readonly settings = signal<SettingsView>({ HotelCode: '', HotelName: '', VmApiBaseUrl: 'https://login.vmcloudpms.com' });
  readonly vmSession = signal<VmSessionView>({ active: false, propertyId: null, baseUrl: null, username: null });

  private pollHandle: ReturnType<typeof setTimeout> | null = null;
  private mergePromptShownForJobId: number | null = null;

  async ngOnInit(): Promise<void> {
    await this.loadContext();
    const jobId = Math.max(0, Number(this.route.snapshot.queryParamMap.get('job') ?? 0) || 0);
    if (jobId > 0) {
      await this.loadJob(jobId);
    } else {
      await this.loadOverview();
    }
  }

  ngOnDestroy(): void {
    this.clearPoll();
  }

  progressPercent(): number {
    const current = this.job();
    if (!current?.total) return 0;
    return Math.max(0, Math.min(100, Math.round((current.processed / current.total) * 100)));
  }

  openSendAllPrompt(): void {
    if (this.busy()) return;
    if (this.overview().failed <= 0) {
      void this.start('send_new', false);
      return;
    }
    this.startModePromptOpen.set(true);
  }

  cancelSendAllPrompt(): void {
    this.startModePromptOpen.set(false);
  }

  async startSendAll(includeFailed: boolean): Promise<void> {
    this.startModePromptOpen.set(false);
    await this.start('send_new', includeFailed);
  }

  async start(mode: 'send_new' | 'update_existing', includeFailed = false): Promise<void> {
    this.clearPoll();
    this.busy.set(true);
    this.message.set('');
    this.isError.set(false);
    try {
      const res = await firstValueFrom(this.api.post<{ needRelogin?: boolean; job?: BulkJob }>('/reservations/bulk-job/start', { mode, includeFailed }));
      if (res.needRelogin) {
        await this.router.navigateByUrl('/import/vm-auth?next=bulk');
        return;
      }
      if (!res.job) {
        this.isError.set(true);
        this.message.set('Bulk send could not be started.');
        return;
      }
      this.job.set(res.job);
      await this.router.navigate([], { relativeTo: this.route, queryParams: { job: res.job.jobId }, queryParamsHandling: 'merge' });
      this.message.set('Bulk job started.');
      if (res.job.status === 'running') this.schedulePoll();
      else {
        await this.loadOverview();
        this.maybeAutoOpenMergePrompt(res.job);
      }
    } catch {
      this.isError.set(true);
      this.message.set('Bulk send could not be started.');
    } finally {
      this.busy.set(false);
    }
  }

  async cancel(): Promise<void> {
    const current = this.job();
    if (!current) return;
    this.busy.set(true);
    try {
      const res = await firstValueFrom(this.api.post<{ job: BulkJob }>(`/reservations/bulk-job/${current.jobId}/cancel`, {}));
      this.job.set(res.job);
      this.message.set('Bulk send canceled.');
      this.clearPoll();
      await this.loadOverview();
    } catch {
      this.isError.set(true);
      this.message.set('Cancel failed.');
    } finally {
      this.busy.set(false);
    }
  }

  async goLanding(): Promise<void> {
    this.clearPoll();
    this.job.set(null);
    this.mergeConfirmOpen.set(false);
    this.mergePromptShownForJobId = null;
    await this.router.navigate([], { relativeTo: this.route, queryParams: { job: null }, queryParamsHandling: 'merge' });
    await this.loadOverview();
  }

  openMergePrompt(): void {
    const current = this.job();
    if (!current || current.status === 'running' || current.succeeded <= 0) return;
    this.mergeConfirmOpen.set(true);
  }

  cancelMerge(): void {
    this.mergeConfirmOpen.set(false);
  }

  async confirmMerge(): Promise<void> {
    const current = this.job();
    this.mergeConfirmOpen.set(false);
    if (!current || current.status === 'running') return;
    this.busy.set(true);
    this.message.set('');
    this.isError.set(false);
    try {
      const res = await firstValueFrom(this.api.post<{ result: { needRelogin?: boolean; summary?: MergeSummary } }>(`/reservations/bulk-job/${current.jobId}/merge-duplicates`, {}));
      if (res.result.needRelogin) {
        await this.router.navigateByUrl('/import/vm-auth?next=bulk');
        return;
      }
      const summary = res.result.summary;
      if (!summary) {
        this.message.set('Duplicate merge completed.');
      } else {
        this.message.set(
          `Duplicate merge complete: checked ${summary.namesChecked} last name(s), attempted ${summary.mergeCallsAttempted} merge call(s), succeeded ${summary.mergeCallsSucceeded}, failed ${summary.mergeCallsFailed}.`,
        );
      }
      this.isError.set(Boolean(summary && summary.mergeCallsFailed > 0));
    } catch {
      this.isError.set(true);
      this.message.set('Guest duplicate merge failed.');
    } finally {
      this.busy.set(false);
    }
  }

  private async loadOverview(): Promise<void> {
    const res = await firstValueFrom(this.api.get<{ overview: BulkOverview }>('/reservations/bulk-overview'));
    this.overview.set(res.overview);
  }

  private async loadContext(): Promise<void> {
    const [ctxRes, vmRes] = await Promise.all([
      firstValueFrom(this.api.get<{ activeProperty: { code: string; name: string; vmApiBaseUrl: string } | null }>('/context/property')),
      firstValueFrom(this.api.get<VmSessionView>('/vm/session')),
    ]);
    this.settings.set({
      HotelCode: ctxRes.activeProperty?.code || '',
      HotelName: ctxRes.activeProperty?.name || '',
      VmApiBaseUrl: ctxRes.activeProperty?.vmApiBaseUrl || 'https://login.vmcloudpms.com',
    });
    this.vmSession.set(vmRes);
  }

  private async loadJob(jobId: number): Promise<void> {
    this.clearPoll();
    const res = await firstValueFrom(this.api.get<{ job: BulkJob | null }>(`/reservations/bulk-job?job=${jobId}`));
    this.job.set(res.job);
    if (!res.job) {
      await this.goLanding();
      return;
    }
    if (res.job.status === 'running') {
      this.schedulePoll();
    } else {
      await this.loadOverview();
      this.maybeAutoOpenMergePrompt(res.job);
    }
  }

  private schedulePoll(): void {
    this.clearPoll();
    this.pollHandle = setTimeout(() => void this.runTick(), 1000);
  }

  private clearPoll(): void {
    if (this.pollHandle) clearTimeout(this.pollHandle);
    this.pollHandle = null;
  }

  private async runTick(): Promise<void> {
    const current = this.job();
    if (!current || current.status !== 'running') return;
    try {
      const res = await firstValueFrom(this.api.post<{ needRelogin?: boolean; job: BulkJob | null }>(`/reservations/bulk-job/${current.jobId}/run`, {}));
      if (res.needRelogin) {
        this.clearPoll();
        await this.router.navigateByUrl('/import/vm-auth?next=bulk');
        return;
      }
      this.job.set(res.job);
      if (res.job?.status === 'running') {
        this.schedulePoll();
      } else {
        await this.loadOverview();
        if (res.job) {
          this.message.set(`Bulk send ${res.job.status}. Succeeded ${res.job.succeeded}, failed ${res.job.failed}, skipped ${res.job.skipped}.`);
          this.isError.set(res.job.status === 'failed');
          this.maybeAutoOpenMergePrompt(res.job);
        }
      }
    } catch {
      this.clearPoll();
      this.isError.set(true);
      this.message.set('Bulk send tick failed.');
    }
  }

  private maybeAutoOpenMergePrompt(job: BulkJob): void {
    if (job.status === 'running' || job.succeeded <= 0) return;
    if (this.mergePromptShownForJobId === job.jobId) return;
    this.mergePromptShownForJobId = job.jobId;
    this.mergeConfirmOpen.set(true);
  }
}
